import dayjs from "dayjs";
import { GetStaticPathsContext, GetStaticProps } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import ReactMarkdown from "react-markdown";
import {
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";
import Particles, { ISourceOptions } from "react-tsparticles";
import { useMeasure } from "react-use";
import { useTranslations } from "use-intl";
import Avatar from "../../common/components/Avatar";
import { BacklitImage } from "../../common/components/BacklitImage";
import Banner from "../../common/components/Banner";
import Header, { makeLink } from "../../common/components/Header";
import { MediaComponent } from "../../common/components/Media";
import Seo from "../../common/components/seo";
import { BlogPostSEO } from "../../common/components/SEO/blog-post-seo";
import { NonMaybe } from "../../common/lib";
import { fetchAPI } from "../../common/lib/api";
import { useBreakpoint } from "../../common/lib/hooks/useWindow";
import { getStrapiMedia } from "../../common/lib/media";
import {
  getLocaleFromStaticContext,
  handleChangeLocale,
} from "../../common/lib/utils";
import particleConfig from "../../common/particlesjs-config.json";
import {
  HEADER_HEIGHT_MEDIUM,
  HEADER_HEIGHT_MOBILE,
} from "../../common/styles/mixins";
import { Article } from "../../generated";
import { Content, Main } from "../../styles/Article";
const ArticlePage = ({
  article,
  localizations,
}: {
  article: NonMaybe<Article>;
  localizations: Article[];
}) => {
  const t = useTranslations("Article");
  const isMedium = useBreakpoint("MEDIUM");
  const [ref, dim] = useMeasure<any>();
  const imageUrl = article.image ? getStrapiMedia(article.image) : "";
  const seo = {
    metaTitle: article.title,
    metaDescription: article.description,
    shareImage: article.image,
    article: true,
  };
  const canonicalUrl = makeLink(`/articles/${article.slug}`);
  const { locale, ...rest } = useRouter();
  const enLocalization =
    article.locale === "en"
      ? article
      : localizations.find((loc) => loc.locale === "en");
  const ptLocalization =
    article.locale === "pt"
      ? article
      : localizations.find((loc) => loc.locale === "pt");
  const isTranslated =
    (locale === "en" && ptLocalization) || (locale === "pt" && enLocalization);
  return (
    <>
      <Header
        localeLinkOverrides={{
          en: enLocalization ? `/articles/${enLocalization?.slug}` : "/",
          pt: ptLocalization ? `/articles/${ptLocalization?.slug}` : "/",
        }}
        router={{ locale, ...rest }}
      />
      <Seo seo={seo} />
      <BlogPostSEO
        {...{
          canonicalUrl,
          author: {
            name: article.author?.name || "Anonymous",
          },
          datePublished: article.published_at,
          defaultTitle: article.title,
          description: article.description,
          image: article.image ? getStrapiMedia(article.image) : "",
          organization: {
            logo: makeLink("/logo.png"),
            name: "Spaceship NFT Art Gallery",
            url: "https://spaceshipnft.com",
          },
          title: article.title,
          url: canonicalUrl,
          dateModified: article.updated_at,
        }}
      />
      <Banner header ref={ref}>
        <Particles
          id="tsparticles"
          height={`${
            dim.height +
            (isMedium ? HEADER_HEIGHT_MEDIUM : HEADER_HEIGHT_MOBILE)
          }px`}
          width={"100vw"}
          options={particleConfig as unknown as ISourceOptions}
        />
        <Content>
          {!article.hideArticleImage && (
            <BacklitImage src={imageUrl} size={isMedium ? 550 : "90vw"} />
          )}
          <div style={{ flex: 1 }} className="glass">
            <h1>{article.title}</h1>
            <p>{article.description}</p>
            <span className="publishing_date">
              Published {dayjs(article.published_at).format("MMMM Do, YYYY")}
            </span>
            {isTranslated && (
              <div className="translated">
                <Link
                  href={`/articles/${isTranslated.slug}`}
                  locale={locale === "en" ? "pt" : "en"}
                >
                  <a onClick={handleChangeLocale}>{t("otherLanguage")}</a>
                </Link>
              </div>
            )}
            <div className="share-icons">
              <TwitterShareButton url={makeLink(`/news${rest.asPath}`)}>
                <TwitterIcon size={28} round />
              </TwitterShareButton>
              <TelegramShareButton url={makeLink(`/news${rest.asPath}`)}>
                <TelegramIcon size={28} round />
              </TelegramShareButton>
              <WhatsappShareButton url={makeLink(`/news${rest.asPath}`)}>
                <WhatsappIcon size={28} round />
              </WhatsappShareButton>
              <RedditShareButton url={makeLink(`/news${rest.asPath}`)}>
                <RedditIcon size={28} round />
              </RedditShareButton>
            </div>
          </div>
        </Content>
        <div className="shadow" />
      </Banner>
      <Main style={{ position: "relative", zIndex: 1 }}>
        <div>
          <div>
            {article.body?.map((item, i) => {
              switch (item.__component) {
                case "article.image":
                  const url =
                    (isMedium
                      ? item.asset?.formats?.large?.url
                      : item.asset?.formats?.medium?.url) ||
                    item.asset?.url ||
                    "";
                  const size = item.size;
                  const width =
                    size === "small"
                      ? "50%"
                      : size === "medium"
                      ? "80%"
                      : "100%";
                  return (
                    <MediaComponent
                      src={url}
                      style={{
                        aspectRatio: `${item.asset?.width || 0} / ${
                          item.asset?.height || 1
                        }`,
                        width,
                        margin: "auto",
                      }}
                      alt={item.alt_text}
                    />
                  );
                case "article.text-area":
                  return <ReactMarkdown>{item.content}</ReactMarkdown>;
                default:
                  console.log(`Returning default`, { item });
                  return null;
              }
            })}
            {article.author && (
              <div className="author">
                {article.author?.picture && (
                  <Avatar
                    src={article.author?.picture.url}
                    size={62}
                    glow="off"
                  />
                )}
                <div className="top_text">
                  <span>Author</span>
                  <div>
                    {article?.author?.name?.split(" ").map((i, n) => (
                      <h4 key={n}>{i}</h4>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Main>
    </>
  );
};

export async function getStaticPaths(ctx: GetStaticPathsContext) {
  const articles = (await fetchAPI("/articles?_locale=all")) as NonMaybe<
    Article[]
  >;
  return {
    paths: articles.map((article) => ({
      params: {
        slug: article.slug,
      },
      locale: article.locale,
    })),
    fallback: "blocking",
  };
}

export const getStaticProps: GetStaticProps<any, { slug: string }> = async (
  ctx
) => {
  try {
    const { params } = ctx;
    const locale = getLocaleFromStaticContext(ctx);
    const articles: NonMaybe<Article>[] = await fetchAPI(
      `/articles?slug=${params?.slug}&_locale=${locale}`
    );
    if (!articles[0])
      return {
        notFound: true,
      };
    const localizations: Article[] = await Promise.all(
      articles[0].localizations?.map((localization) =>
        fetchAPI(
          `/articles?id=${localization?.id}&_locale=${localization?.locale}`
        )
      ) || []
    ).then((res: Article[][]) => {
      const base = [] as Article[];
      return base.concat(...res);
    });
    return {
      props: { article: articles[0], localizations },
      revalidate: 1,
    };
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }
};

export default ArticlePage;
